import React, { useState, useRef, useEffect } from "react";
import { FiCalendar, FiCheck, FiChevronDown, FiX } from "react-icons/fi";
import {
  PRESETS,
  PRESET_LABELS,
  calculatePresetRange,
  validateDateRange,
} from "../../utils/dateFilterUtils";
import "../../assets/css/Common/DateFilter.css";

/**
 * Common reusable Date Filter component.
 *
 * @param {Object} props
 * @param {Object} props.value Current date filter state { start_date, end_date, preset }
 * @param {Function} props.onChange Callback invoked when date filter changes
 * @param {boolean} [props.disabled=false] Whether filter is disabled
 * @param {string} [props.className=""] Additional CSS class
 */
export default function DateFilter({
  value,
  onChange,
  disabled = false,
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [customStart, setCustomStart] = useState(value?.start_date || "");
  const [customEnd, setCustomEnd] = useState(value?.end_date || "");
  const [showCustomInputs, setShowCustomInputs] = useState(
    value?.preset === PRESETS.CUSTOM
  );
  const [validationError, setValidationError] = useState("");

  const containerRef = useRef(null);

  // Sync internal custom range inputs when value prop changes externally
  useEffect(() => {
    if (value?.start_date) setCustomStart(value.start_date);
    if (value?.end_date) setCustomEnd(value.end_date);
    setShowCustomInputs(value?.preset === PRESETS.CUSTOM);
  }, [value?.start_date, value?.end_date, value?.preset]);

  // Dismiss dropdown on outside click or Escape
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSelectPreset = (presetKey) => {
    if (presetKey === PRESETS.CUSTOM) {
      setShowCustomInputs(true);
      setValidationError("");
      return;
    }

    setShowCustomInputs(false);
    setValidationError("");
    const newRange = calculatePresetRange(presetKey);
    onChange?.(newRange);
    setIsOpen(false);
  };

  const handleApplyCustom = (e) => {
    e?.preventDefault();
    const validation = validateDateRange(customStart, customEnd);
    if (!validation.isValid) {
      setValidationError(validation.error);
      return;
    }

    if (!customStart && !customEnd) {
      setValidationError("Please select at least a start or end date");
      return;
    }

    setValidationError("");
    onChange?.({
      start_date: customStart,
      end_date: customEnd,
      preset: PRESETS.CUSTOM,
    });
    setIsOpen(false);
  };

  const handleClear = () => {
    setCustomStart("");
    setCustomEnd("");
    setValidationError("");
    setShowCustomInputs(false);
    onChange?.({
      start_date: "",
      end_date: "",
      preset: PRESETS.ALL,
    });
    setIsOpen(false);
  };

  // Determine button label text
  const getDisplayLabel = () => {
    if (!value || value.preset === PRESETS.ALL || (!value.start_date && !value.end_date)) {
      return "Filter by Date";
    }

    if (value.preset === PRESETS.CUSTOM) {
      if (value.start_date && value.end_date) {
        return `${value.start_date} to ${value.end_date}`;
      }
      if (value.start_date) return `From ${value.start_date}`;
      if (value.end_date) return `Until ${value.end_date}`;
    }

    return PRESET_LABELS[value.preset] || "Filter by Date";
  };

  const isFiltered = Boolean(
    value &&
      value.preset !== PRESETS.ALL &&
      (value.start_date || value.end_date)
  );

  const presetOptions = [
    { key: PRESETS.TODAY, label: PRESET_LABELS[PRESETS.TODAY] },
    { key: PRESETS.LAST_7_DAYS, label: PRESET_LABELS[PRESETS.LAST_7_DAYS] },
    { key: PRESETS.LAST_30_DAYS, label: PRESET_LABELS[PRESETS.LAST_30_DAYS] },
    { key: PRESETS.THIS_MONTH, label: PRESET_LABELS[PRESETS.THIS_MONTH] },
    { key: PRESETS.THIS_QUARTER, label: PRESET_LABELS[PRESETS.THIS_QUARTER] },
    { key: PRESETS.YTD, label: PRESET_LABELS[PRESETS.YTD] },
    { key: PRESETS.CUSTOM, label: PRESET_LABELS[PRESETS.CUSTOM] },
  ];

  return (
    <div
      className={`date-filter-container ${className}`}
      ref={containerRef}
      data-testid="date-filter-container"
    >
      <button
        type="button"
        className={`date-filter-trigger ${isFiltered ? "active" : ""}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-testid="date-filter-trigger"
      >
        <FiCalendar className="date-filter-icon" />
        <span className="date-filter-label" data-testid="date-filter-label">
          {getDisplayLabel()}
        </span>
        {isFiltered && (
          <span
            className="date-filter-clear-badge"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            title="Clear date filter"
            data-testid="date-filter-quick-clear"
          >
            <FiX style={{ fontSize: "0.85rem", cursor: "pointer" }} />
          </span>
        )}
        <FiChevronDown style={{ fontSize: "0.85rem", marginLeft: "2px" }} />
      </button>

      {isOpen && (
        <div className="date-filter-popover" data-testid="date-filter-popover">
          <div className="date-filter-popover-header">Date Range</div>

          <ul className="date-filter-presets">
            {presetOptions.map((opt) => {
              const isSelected = value?.preset === opt.key;
              return (
                <li
                  key={opt.key}
                  className={`date-filter-preset-item ${
                    isSelected ? "selected" : ""
                  }`}
                  onClick={() => handleSelectPreset(opt.key)}
                  data-testid={`preset-${opt.key}`}
                >
                  <span>{opt.label}</span>
                  {isSelected && <FiCheck className="date-filter-check" />}
                </li>
              );
            })}
          </ul>

          {showCustomInputs && (
            <div
              className="date-filter-custom-section"
              data-testid="custom-range-section"
            >
              <div className="date-filter-custom-title">Select Custom Range</div>

              <div className="date-filter-input-group">
                <label htmlFor="df-start-date">Start Date</label>
                <input
                  id="df-start-date"
                  type="date"
                  value={customStart}
                  onChange={(e) => {
                    setCustomStart(e.target.value);
                    setValidationError("");
                  }}
                  data-testid="custom-start-input"
                />
              </div>

              <div className="date-filter-input-group">
                <label htmlFor="df-end-date">End Date</label>
                <input
                  id="df-end-date"
                  type="date"
                  value={customEnd}
                  onChange={(e) => {
                    setCustomEnd(e.target.value);
                    setValidationError("");
                  }}
                  data-testid="custom-end-input"
                />
              </div>

              {validationError && (
                <div
                  className="date-filter-error"
                  role="alert"
                  data-testid="custom-date-error"
                >
                  {validationError}
                </div>
              )}

              <div className="date-filter-custom-actions">
                <button
                  type="button"
                  className="date-filter-btn date-filter-btn-secondary"
                  onClick={() => setShowCustomInputs(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="date-filter-btn date-filter-btn-primary"
                  onClick={handleApplyCustom}
                  data-testid="apply-custom-date"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <div className="date-filter-footer">
            <button
              type="button"
              className="date-filter-clear-btn"
              onClick={handleClear}
              data-testid="clear-date-filter"
            >
              Clear Filter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
